import {
  MenuItem,
  Typography,
  // eslint-disable-next-line no-restricted-imports
  type SelectChangeEvent,
  Box,
  Chip,
  type MenuItemProps,
  Checkbox,
  IconButton,
  Tooltip,
} from "@mui/material";
import { type ChangeEvent, forwardRef, useId, useMemo } from "react";
import { Input, type InputProps } from "./Input";
import Close from "@mui/icons-material/Close";

type Primitive = number | string;
export { type SelectChangeEvent };
export interface SelectProps
  extends Omit<
    InputProps,
    "native" | "children" | "input" | "inputProps" | "onChange"
  > {
  options?: Array<string | { name: string; value: Primitive }>;
  /** Whether *None* is a valid option. Has no effect when multiple is `true`. */
  allowNull?: boolean;
  /** Label for the *None* option rendered when `allowNull` is set. */
  nullOptionLabel?: string;
  onChange?: (e: SelectChangeEvent) => void;
  elevation?: number;
  multiple?: boolean;
  displaySelectAllOption?: boolean;
  selectAllOptionLabel?: string;
}

function asValueArray(value: unknown): Primitive[] {
  if (Array.isArray(value)) return value as Primitive[];
  return [];
}

const SelectMenuItem: React.FC<
  React.PropsWithChildren<
    MenuItemProps & {
      showCheckBox?: boolean;
      checked?: boolean;
      indeterminate?: boolean;
    }
  >
> = ({
  showCheckBox = false,
  checked = false,
  indeterminate,
  children,
  value,
  ...rest
}) => {
  return (
    <MenuItem value={value} {...rest}>
      {showCheckBox ? (
        <>
          <Checkbox
            checked={checked}
            indeterminate={indeterminate}
            tabIndex={-1}
            disableRipple
            sx={{ py: 0, pl: 0, pr: 2 }}
          />
          {children}
        </>
      ) : (
        children
      )}
    </MenuItem>
  );
};

export const Select = forwardRef<any, SelectProps>(
  (
    {
      fullWidth = true,
      label: userLabel,
      allowNull = true,
      nullOptionLabel = "None",
      options = [],
      slotProps,
      sx,
      onChange,
      elevation,
      multiple,
      displaySelectAllOption,
      selectAllOptionLabel = "Select All",
      value,
      ...rest
    },
    ref
  ) => {
    const ranId = useId();
    const selectAllId = `selectall-${ranId}`;
    const computedOptions = useMemo(() => {
      const res = options.map((item) => {
        if (typeof item === "string") {
          return {
            name: item,
            value: item,
          };
        }
        return item;
      });
      return res;
    }, [options]);

    const selectedValues = asValueArray(value);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      if (multiple) {
        const raw = e.target.value as unknown;
        const values = Array.isArray(raw)
          ? (raw as Primitive[])
          : typeof raw === "string" && raw.length > 0
          ? raw.split(",")
          : [];
        if (values.includes(selectAllId)) {
          e.target.value = (
            selectedValues.length === computedOptions.length
              ? []
              : computedOptions.map((x) => x.value)
          ) as any;
        }
      }
      onChange?.(e);
    };

    return (
      <Input
        {...rest}
        select
        slotProps={{
          ...slotProps,
          input: {
            ...(slotProps?.input ?? {}),
            endAdornment: (
              <>
                {(slotProps?.input as any)?.endAdornment}
                {multiple && selectedValues.length > 0 && (
                  <Tooltip title="Clear">
                    <IconButton
                      size="small"
                      sx={{ mr: 1.5 }}
                      aria-label="Clear Selection"
                      onClick={() =>
                        onChange?.({ target: { value: [] } } as any)
                      }
                    >
                      <Close />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            ),
          },
          select: {
            ...(slotProps?.select ?? {}),
            MenuProps: { elevation },
            multiple,
            id: rest.name ? `${rest.name}-select` : undefined,
            renderValue: multiple
              ? (selected) => {
                  const selectedItems = asValueArray(selected);
                  // Collapse "all selected" to the Select All label instead of
                  // one chip per option (the Project Overview default)
                  if (
                    displaySelectAllOption &&
                    computedOptions.length > 0 &&
                    selectedItems.length === computedOptions.length
                  ) {
                    return selectAllOptionLabel;
                  }
                  return (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {[...selectedItems]
                        .sort((x, y) =>
                          (
                            computedOptions.find((z) => z.value === x)?.name ??
                            ""
                          ).localeCompare(
                            computedOptions.find((z) => z.value === y)?.name ??
                              ""
                          )
                        )
                        ?.map((itemValue) => {
                          return (
                            <Chip
                              key={String(itemValue)}
                              size="small"
                              label={
                                computedOptions.find(
                                  (x) => x.value === itemValue
                                )?.name
                              }
                            />
                          );
                        })}
                    </Box>
                  );
                }
              : undefined,
          },
        }}
        label={userLabel}
        fullWidth={fullWidth}
        value={value ?? (multiple ? [] : undefined)}
        onChange={handleChange}
        sx={sx}
        ref={ref}
      >
        {allowNull && !multiple && (
          <SelectMenuItem value="">
            <Typography component="span" sx={{ fontStyle: "italic" }}>
              {nullOptionLabel}
            </Typography>
          </SelectMenuItem>
        )}
        {multiple && displaySelectAllOption && (
          <SelectMenuItem
            value={selectAllId}
            showCheckBox
            indeterminate={
              selectedValues.length > 0 &&
              selectedValues.length < computedOptions.length
            }
            checked={
              computedOptions.length > 0 &&
              selectedValues.length === computedOptions.length
            }
          >
            {selectAllOptionLabel}
          </SelectMenuItem>
        )}
        {computedOptions.map((item, index) => {
          return (
            <SelectMenuItem
              value={item.value}
              key={item.name + index}
              showCheckBox={multiple}
              checked={multiple ? selectedValues.includes(item.value) : false}
            >
              {item.name}
            </SelectMenuItem>
          );
        })}
      </Input>
    );
  }
);
